import { todayInPoland } from './dateUtils';

/** Lexical compare ok for Gregorian YYYY-MM-DD. */
export function compareYMD(a: string, b: string): number {
    return a.localeCompare(b);
}

export function maxYMD(...vals: string[]): string {
    return vals.reduce((m, x) => (compareYMD(x, m) > 0 ? x : m));
}

export function minYMD(...vals: string[]): string {
    return vals.reduce((m, x) => (compareYMD(x, m) < 0 ? x : m));
}

export function formatLocalYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function addDaysYMD_Local(ymd: string, deltaDays: number): string {
    const [y, mo, da] = ymd.split('-').map(Number);
    const d = new Date(y, mo - 1, da);
    d.setDate(d.getDate() + deltaDays);
    return formatLocalYMD(d);
}

export function firstDayOfCalendarMonth(year: number, month1: number): string {
    return `${year}-${String(month1).padStart(2, '0')}-01`;
}

export function lastDayOfCalendarMonth(year: number, month1: number): string {
    return formatLocalYMD(new Date(year, month1, 0));
}

/**
 * Uniform random calendar day inclusive [startYmd, endYmd].
 * Preconditions: compareYMD(startYmd, endYmd) <= 0.
 */
export function uniformRandomInclusiveDay(startYmd: string, endYmd: string): string {
    const [ys, ms, ds] = startYmd.split('-').map(Number);
    const [ye, me, de] = endYmd.split('-').map(Number);
    const start = Date.UTC(ys, ms - 1, ds);
    const end = Date.UTC(ye, me - 1, de);
    const spanDays = Math.floor((end - start) / 86400000);
    const offset = Math.floor(Math.random() * (spanDays + 1));
    const picked = new Date(start + offset * 86400000);
    return formatUTCYMD(picked);
}

function formatUTCYMD(d: Date): string {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Intersects [logicalStart, logicalEnd] with [pubFrom, pubTo] and caps at Warsaw "today".
 * If empty, picks the latest day still plausible (typically min(ends)).
 */
export function pickPublicationCalendarDay(opts: {
    logicalPublicationStart: string;
    logicalPublicationEnd: string;
    pubDateFrom: string;
    pubDateTo: string;
    /** When null/undefined, uses todayInPoland() */
    capToday?: string | null;
}): { dayYmd: string; usedIntersection: boolean } {
    const todayCap = opts.capToday ?? todayInPoland();
    const minDay = maxYMD(opts.logicalPublicationStart, opts.pubDateFrom);
    const maxDay = minYMD(opts.logicalPublicationEnd, opts.pubDateTo, todayCap);

    if (compareYMD(minDay, maxDay) <= 0) {
        return { dayYmd: uniformRandomInclusiveDay(minDay, maxDay), usedIntersection: true };
    }

    /** Conflicting bounds (rare): still avoid future timestamps relative to todayCap */
    return { dayYmd: maxDay, usedIntersection: false };
}

/**
 * Builds a local Date from YYYY-MM-DD + random HH:mm:ss in [timeFrom,timeTo].
 */
export function randomLocalTimestampOnCalendarDay(dayYmd: string, timeFrom: string, timeTo: string): Date {
    const [fromH, fromM] = timeFrom.split(':').map(Number);
    const [toH, toM] = timeTo.split(':').map(Number);
    const fromMinutes = fromH * 60 + fromM;
    const toMinutes = toH * 60 + toM;
    const randomMinutes = fromMinutes + Math.random() * (toMinutes - fromMinutes);
    const h = Math.floor(randomMinutes / 60);
    const m = Math.floor(randomMinutes % 60);
    const s = Math.floor(Math.random() * 60);

    const [y, mo, da] = dayYmd.split('-').map(Number);
    return new Date(y, mo - 1, da, h, m, s);
}
